import React, { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, MapPin, Phone, Mail, Send } from "lucide-react";
import { COMPANY, SERVICES, buildWhatsAppLink } from "@/lib/constants";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    topic: SERVICES[0].title,
    message: "",
  });

  const update = (key) => (e) => setForm((s) => ({ ...s, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = `Hallo AutoCoach24,

mein Name ist ${form.name || "—"}.
Anliegen: ${form.topic}

${form.message || "(Bitte melden Sie sich bei mir.)"}

Vielen Dank!`;
    const url = buildWhatsAppLink(text);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#E6F6F8] rounded-full blur-3xl opacity-60 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left side info */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#1FA3B5] mb-4">Kontakt</p>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.1]">
              Reden wir über<br />
              <span className="text-[#1FA3B5]">Ihr Auto.</span>
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Schreiben Sie kurz, worum es geht – wir melden uns in der Regel innerhalb weniger Stunden zurück.
              Das Erstgespräch ist kostenlos.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-[#25D366] hover:bg-[#25D366]/5 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#25D366] flex items-center justify-center text-white group-hover:scale-110 transition-transform">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Bevorzugter Kontaktweg</p>
                  <p className="font-heading font-semibold text-slate-900">WhatsApp · {COMPANY.phoneDisplay}</p>
                </div>
              </a>

              <a
                href={`tel:${COMPANY.phoneIntl.replace(/\s/g, "")}`}
                data-testid="contact-phone-link"
                className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-[#1FA3B5] hover:bg-[#E6F6F8]/50 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-[#E6F6F8] flex items-center justify-center text-[#1FA3B5]">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Telefon</p>
                  <p className="font-heading font-semibold text-slate-900">{COMPANY.phoneDisplay}</p>
                </div>
              </a>

              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-[#1FA3B5] hover:bg-[#E6F6F8]/50 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-[#E6F6F8] flex items-center justify-center text-[#1FA3B5]">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">E-Mail</p>
                  <p className="font-heading font-semibold text-slate-900">{COMPANY.email}</p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200">
                <div className="w-12 h-12 rounded-xl bg-[#E6F6F8] flex items-center justify-center text-[#1FA3B5]">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Standort</p>
                  <p className="font-heading font-semibold text-slate-900">{COMPANY.street}, {COMPANY.zip} {COMPANY.city}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right side – form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <form
            onSubmit={handleSubmit}
            data-testid="whatsapp-contact-form"
            className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-900/5 p-8 lg:p-10"
          >
            <h3 className="font-heading text-2xl lg:text-3xl font-semibold text-slate-900">
              Direkt per WhatsApp anfragen
            </h3>
            <p className="mt-2 text-slate-600 text-sm">
              Mit Klick öffnet sich WhatsApp mit Ihrer vorbereiteten Nachricht – Sie senden sie selbst ab.
            </p>

            <div className="mt-8 space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  Ihr Name
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-form-name"
                  placeholder="Max Mustermann"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-slate-900 placeholder:text-slate-400 focus:border-[#1FA3B5] focus:ring-4 focus:ring-[#1FA3B5]/15 outline-none transition-all"
                />
              </div>

              <div>
                <label htmlFor="topic" className="block text-sm font-medium text-slate-700 mb-2">
                  Anliegen
                </label>
                <select
                  id="topic"
                  value={form.topic}
                  onChange={update("topic")}
                  data-testid="contact-form-topic"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-slate-900 focus:border-[#1FA3B5] focus:ring-4 focus:ring-[#1FA3B5]/15 outline-none transition-all"
                >
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title}>{s.title}</option>
                  ))}
                  <option value="Sonstiges Anliegen">Sonstiges Anliegen</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                  Ihre Nachricht
                </label>
                <textarea
                  id="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-form-message"
                  placeholder="Kurz worum es geht – z. B. Fahrzeugtyp, Termin, Frage..."
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-slate-900 placeholder:text-slate-400 focus:border-[#1FA3B5] focus:ring-4 focus:ring-[#1FA3B5]/15 outline-none transition-all resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-form-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1DAE54] text-white rounded-full px-8 py-4 font-semibold transition-all hover:-translate-y-0.5 shadow-xl shadow-[#25D366]/30"
              >
                <Send className="w-5 h-5" />
                Per WhatsApp senden
              </button>

              <p className="text-xs text-slate-500 text-center">
                Mit dem Klick öffnet sich WhatsApp – Ihre Daten verlassen die Seite nicht im Voraus.
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
